import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Mail, RotateCw } from "lucide-react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SEO } from "@/components/seo/SEO";
import { api } from "@/services/api/client";
import {
  getPublicAuthChallengeFailureMessage,
  getPublicAuthChallengeToken,
  isPublicAuthChallengeFailure,
} from "@/services/api/publicAuthChallenge";
import type {
  ApiResponse,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
} from "@/types";

type ForgotPasswordState =
  | "idle"
  | "sending"
  | "sent"
  | "failed"
  | "challenge-failed";

const FORGOT_PASSWORD_ACCEPTED_MESSAGE =
  "If an account exists for that email, a password reset link will be sent.";

const isForgotPasswordAcceptedResponse = (
  response: ApiResponse<ForgotPasswordResponse>,
): boolean =>
  response.success === true &&
  response.data?.sent === true &&
  response.data.status === "accepted";

const getMessageAccessibility = (
  state: ForgotPasswordState,
): {
  role: "status" | "alert";
  live: "polite" | "assertive";
  className: string;
} => {
  switch (state) {
    case "sent":
      return {
        role: "status",
        live: "polite",
        className:
          "rounded-md border border-emerald-500/20 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-700 dark:text-emerald-400",
      };
    case "failed":
    case "challenge-failed":
      return {
        role: "alert",
        live: "assertive",
        className:
          "rounded-md border border-destructive/20 bg-destructive/10 px-3 py-2 text-sm text-destructive",
      };
    case "idle":
    case "sending":
    default:
      return {
        role: "status",
        live: "polite",
        className:
          "rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground",
      };
  }
};

export const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState("");
  const [requestState, setRequestState] = useState<ForgotPasswordState>("idle");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      return;
    }

    setRequestState("sending");

    try {
      const publicAuthChallengeToken =
        await getPublicAuthChallengeToken("forgot-password");
      const response = await api.post<
        ForgotPasswordResponse,
        ForgotPasswordRequest
      >(
        "/auth/forgot-password",
        publicAuthChallengeToken
          ? { email: trimmedEmail, publicAuthChallengeToken }
          : { email: trimmedEmail },
      );

      setRequestState(
        isForgotPasswordAcceptedResponse(response) ? "sent" : "failed",
      );
    } catch (error) {
      setRequestState(
        isPublicAuthChallengeFailure(error) ? "challenge-failed" : "failed",
      );
    }
  };

  const requestMessage = (() => {
    switch (requestState) {
      case "sent":
        return FORGOT_PASSWORD_ACCEPTED_MESSAGE;
      case "challenge-failed":
        return getPublicAuthChallengeFailureMessage();
      case "failed":
        return "We could not process that reset request. Please try again later.";
      case "idle":
      case "sending":
      default:
        return "";
    }
  })();
  const messageAccessibility = getMessageAccessibility(requestState);

  return (
    <>
      <SEO
        title="Reset Your TerraMatrix Password"
        description="Request a TerraMatrix password reset link."
        canonical="/forgot-password"
        noindex={true}
      />
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary" />
            <CardTitle>Reset Password</CardTitle>
          </div>
          <CardDescription>
            Enter your email and we will send reset instructions.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label
                htmlFor="forgot-password-email"
                className="text-sm font-medium"
              >
                Email
              </label>
              <Input
                id="forgot-password-email"
                type="email"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  if (requestState !== "sending") {
                    setRequestState("idle");
                  }
                }}
                placeholder="you@example.com"
                disabled={requestState === "sending"}
                autoComplete="email"
                required
              />
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={requestState === "sending" || email.trim() === ""}
            >
              {requestState === "sending" ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RotateCw className="mr-2 h-4 w-4" />
              )}
              Send reset link
            </Button>
            {requestMessage && (
              <div
                role={messageAccessibility.role}
                aria-live={messageAccessibility.live}
                aria-atomic="true"
                className={messageAccessibility.className}
              >
                {requestMessage}
              </div>
            )}
          </form>
          <Button asChild variant="ghost" className="w-full">
            <Link to="/login">Back to sign in</Link>
          </Button>
        </CardContent>
      </Card>
    </>
  );
};
